import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { useSWRConfig } from "swr";

import { ShipmentLiveUpdateController } from "components/ShipmentLiveUpdateBanner";
import { ShipperShipmentPageProvider } from "context";
import {
  useQuoteCarrierLogos,
  useRoleContext,
  useShipment,
  useShipments,
  useShipperQuoteCompetitivenessScores,
} from "hooks";
import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";
import { getPageCount, revalidateShipmentDetailAndListCaches } from "utils";
import { getActiveFiltersCount } from "utils/shipment";

import { ShipmentOverviewExpanded } from "../ShipmentOverviewExpanded";
import { Shipment } from "./Shipment";

const QUICK_SHIPMENT_BROWSER_PAGE_SIZE = 5;

export function ShipmentContainer() {
  const { mutate: revalidateAll } = useSWRConfig();
  const { role } = useRoleContext();
  const params = useParams();
  const shipmentId = params.id;
  const [page, setPage] = useState<number>(1);

  const {
    shipments,
    isLoading: shipmentsAreLoading,
    count,
    refreshShipments,
  } = useShipments({ page, pageSize: QUICK_SHIPMENT_BROWSER_PAGE_SIZE });
  const { shipment, error: shipmentError } =
    useShipment<ShipperCondensedShipment>({
      shipmentId,
    });
  // Beside useShipment on purpose: shipmentId comes from the URL, so these
  // requests leave with the shipment one rather than waiting for the quotes.
  const { logoByCompanyId } = useQuoteCarrierLogos({ shipmentId });
  // Nothing to show while loading: bands and the competitiveness sorts only
  // appear once scores arrive.
  const { scoreByQuoteId, error: scoresError } =
    useShipperQuoteCompetitivenessScores({ shipmentId });
  const pages = getPageCount(count, QUICK_SHIPMENT_BROWSER_PAGE_SIZE);

  const { registerRefreshCallback, unregisterRefreshCallback, filters } =
    useShipmentFiltersContext("regular");

  const handlePageChange = (
    _e: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
  };

  const refreshShipment = useCallback(() => {
    // Refreshing the list too is intended here: the user asked for a refresh.
    revalidateShipmentDetailAndListCaches(revalidateAll);
  }, [revalidateAll]);

  useEffect(() => {
    registerRefreshCallback(refreshShipments);
    return () => unregisterRefreshCallback();
  }, [registerRefreshCallback, unregisterRefreshCallback, refreshShipments]);

  const activeFiltersCount = getActiveFiltersCount(filters);

  return (
    <ShipperShipmentPageProvider
      shipment={shipment}
      onRefresh={refreshShipment}
      error={shipmentError}
      logoByCompanyId={logoByCompanyId}
      scoreByQuoteId={scoreByQuoteId}
      scoresError={scoresError}
    >
      <Shipment
        shipments={shipments}
        shipmentSelectionIsDisabled={shipmentsAreLoading}
        shipmentOverview={<ShipmentOverviewExpanded />}
        page={page}
        pages={pages}
        handlePageSelected={handlePageChange}
        shipmentCount={count}
        activeFiltersCount={activeFiltersCount}
      />
      <ShipmentLiveUpdateController shipmentId={shipmentId} role={role} />
    </ShipperShipmentPageProvider>
  );
}
